<template>
  <div
    class="modal fade"
    id="employee_gratuity"
    tabindex="-1"
    aria-hidden="true"
    data-bs-backdrop="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Pay gratuity</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p v-if="context" class="alert alert-info">
            Linked to an approved {{ context.source_type === "termination" ? "termination" : "resignation" }}.
            Service ends on the last working day, {{ context.service_end }}.
          </p>
          <p v-if="linkedExisting" class="alert alert-warning">
            A gratuity for this record is already {{ linkedExisting.status }}.
          </p>
          <p class="text-muted mb-3">
            Gratuity earned on or after 1 July 2025 is exempt from PAYE. Gratuity earned
            before that date is taxed over the years it was earned, up to five years, and
            the extra PAYE is withheld in the payroll month you choose. It is not taxed
            again as a normal allowance.
          </p>

          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label">Gratuity amount</label>
              <input v-model.number="form.gross_amount" type="number" min="0" step="0.01" class="form-control" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Service start</label>
              <input v-model="form.service_start" type="date" class="form-control" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Service end</label>
              <input v-model="form.service_end" type="date" class="form-control" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Payroll month</label>
              <input v-model="form.payment_period" type="month" class="form-control" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Pay to</label>
              <select v-model="form.destination" class="form-select">
                <option value="employee">Employee</option>
                <option value="pension_scheme">Registered pension scheme</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Scheme</label>
              <select v-model="form.scheme_type" class="form-select">
                <option value="private">Private employment gratuity</option>
                <option value="public_pension">Public pension scheme</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label">Percent of basic</label>
              <input v-model.number="form.percent" type="number" min="0" step="0.01" class="form-control" />
            </div>
            <div class="col-md-2 d-flex align-items-end">
              <button type="button" class="btn btn-outline-secondary w-100" @click="estimateAmount">
                Estimate
              </button>
            </div>
          </div>
          <small class="text-muted d-block mt-2">
            Estimate uses basic salary {{ money(currentBasic()) }} × percent × completed years of service. You can overwrite the amount.
          </small>

          <div class="d-flex gap-2 mt-3">
            <button type="button" class="btn btn-primary" :disabled="busy" @click="preview">
              Calculate
            </button>
            <button type="button" class="btn btn-success" :disabled="busy || !worksheet || linkedExisting" @click="save">
              Schedule for payroll
            </button>
          </div>
          <p v-if="error" class="text-danger mt-2 mb-0">{{ error }}</p>

          <div v-if="worksheet" class="mt-4">
            <div class="row g-2 mb-3">
              <div class="col-md-3"><strong>Exempt:</strong> {{ money(worksheet.exempt_amount) }}</div>
              <div class="col-md-3"><strong>Taxable:</strong> {{ money(worksheet.taxable_amount) }}</div>
              <div class="col-md-3"><strong>PAYE:</strong> {{ money(worksheet.paye_amount) }}</div>
              <div class="col-md-3"><strong>Net:</strong> {{ money(worksheet.net_amount) }}</div>
            </div>

            <div class="table-responsive">
              <table class="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Gratuity</th>
                    <th>Employment income</th>
                    <th>PAYE already paid</th>
                    <th>Pension deducted</th>
                    <th>Extra PAYE</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="year in worksheet.years" :key="year.year">
                    <td>
                      {{ year.year }}
                      <small v-if="year.bundled_from?.length" class="d-block text-muted">
                        Includes {{ year.bundled_from.join(", ") }}
                      </small>
                    </td>
                    <td>{{ money(year.gratuity_amount) }}</td>
                    <td>
                      <input
                        v-model.number="prior[year.year].employment_income"
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control form-control-sm"
                      />
                    </td>
                    <td>
                      <input
                        v-model.number="prior[year.year].paye_paid"
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control form-control-sm"
                      />
                    </td>
                    <td>
                      <input
                        v-model.number="prior[year.year].pension_deducted"
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control form-control-sm"
                      />
                    </td>
                    <td>{{ money(year.extra_paye) }}</td>
                    <td>{{ year.history_source === "payroll" ? "Payroll" : year.history_source === "manual" ? "Entered" : "No history" }}</td>
                  </tr>
                  <tr v-if="!worksheet.years.length">
                    <td colspan="7">The full amount was earned after the exemption date, so no PAYE is due.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <button type="button" class="btn btn-outline-primary btn-sm" :disabled="busy" @click="preview">
              Recalculate with the income above
            </button>
          </div>

          <div class="mt-4">
            <h6>Scheduled and paid</h6>
            <div v-if="!rows.length" class="text-muted">No gratuity has been scheduled for this employee.</div>
            <table v-else class="table table-sm">
              <thead>
                <tr>
                  <th>Payroll month</th>
                  <th>Amount</th>
                  <th>PAYE</th>
                  <th>Net</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.id">
                  <td>{{ row.payment_period }}</td>
                  <td>{{ money(row.gross_amount) }}</td>
                  <td>{{ money(row.paye_amount) }}</td>
                  <td>{{ money(row.net_amount) }}</td>
                  <td>{{ row.status }}</td>
                  <td class="text-end">
                    <button
                      v-if="row.status === 'scheduled'"
                      type="button"
                      class="btn btn-outline-danger btn-sm"
                      @click="remove(row)"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, reactive, ref, watch } from "vue";
import employeeService from "@/services/hrm/employeeService";
import { toast } from "@/utils/toast";

const props = defineProps({
  employee: { type: Object, default: null },
  context: { type: Object, default: null },
});

const busy = ref(false);
const error = ref("");
const worksheet = ref(null);
const rows = ref([]);
const prior = reactive({});
const linkedExisting = ref(null);
const basicSalary = ref(0);

const form = reactive({
  gross_amount: null,
  service_start: "",
  service_end: "",
  payment_period: currentPeriod(),
  destination: "employee",
  scheme_type: "private",
  percent: 0,
});

function markGratuityOpen() {
  document.body.classList.add("gratuity-modal-open");
}

function markGratuityClosed() {
  document.body.classList.remove("gratuity-modal-open");
}

onMounted(() => {
  const el = document.getElementById("employee_gratuity");
  el?.addEventListener("show.bs.modal", markGratuityOpen);
  el?.addEventListener("hidden.bs.modal", markGratuityClosed);
});

onBeforeUnmount(() => {
  const el = document.getElementById("employee_gratuity");
  el?.removeEventListener("show.bs.modal", markGratuityOpen);
  el?.removeEventListener("hidden.bs.modal", markGratuityClosed);
  markGratuityClosed();
});

watch(
  () => [props.employee?.id, props.context?.source_type, props.context?.source_id, props.context],
  () => {
    worksheet.value = null;
    error.value = "";
    linkedExisting.value = props.context?.existing || null;
    basicSalary.value = Number(props.employee?.basic_salary || props.employee?.salary || 0);
    form.gross_amount = null;
    form.service_start = dateOnly(props.context?.service_start || props.employee?.company_doj);
    form.service_end = dateOnly(props.context?.service_end) || new Date().toISOString().slice(0, 10);
    form.payment_period = props.context?.payment_period || currentPeriod();
    form.destination = "employee";
    form.scheme_type = props.context?.source_type === "termination" && props.context?.scheme_type
      ? props.context.scheme_type
      : "private";
    load();
  },
  { immediate: true },
);

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function dateOnly(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function money(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function completedYears(start, end) {
  const from = parseDate(start);
  const to = parseDate(end);
  if (!from || !to || to < from) return 0;
  let years = to.getFullYear() - from.getFullYear();
  const anniversary = new Date(to.getFullYear(), from.getMonth(), from.getDate());
  if (to < anniversary) years -= 1;
  return Math.max(years, 0);
}

function parseDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function currentBasic() {
  return Number(basicSalary.value || props.employee?.basic_salary || props.employee?.salary || 0);
}

function estimateAmount() {
  const basic = currentBasic();
  const years = completedYears(form.service_start, form.service_end);
  if (basic <= 0) {
    error.value = "This employee has no basic salary on the salary setup, so the percent cannot be estimated. Enter the gratuity amount.";
    return;
  }
  if (years <= 0) {
    error.value = "There are no completed years between these dates, so the estimate stays empty. Enter the gratuity amount.";
    return;
  }
  error.value = "";
  form.gross_amount = Number((basic * (Number(form.percent || 0) / 100) * years).toFixed(2));
}

function priorPayload() {
  return Object.entries(prior).map(([year, row]) => ({
    year: Number(year),
    employment_income: Number(row.employment_income || 0),
    paye_paid: Number(row.paye_paid || 0),
    pension_deducted: Number(row.pension_deducted || 0),
  }));
}

function rememberPrior(nextWorksheet) {
  for (const year of nextWorksheet.years || []) {
    if (!prior[year.year] || year.history_source !== "manual") {
      prior[year.year] = {
        employment_income: Number(year.employment_income || 0),
        paye_paid: Number(year.paye_already_paid || 0),
        pension_deducted: Number(year.pension_already_deducted || 0),
      };
    }
  }
}

async function load() {
  if (!props.employee?.id) {
    rows.value = [];
    return;
  }
  try {
    const response = await employeeService.listGratuities(props.employee.id);
    rows.value = response.data?.data || [];
    basicSalary.value = Number(response.data?.basic_salary || props.employee?.basic_salary || props.employee?.salary || 0);
  } catch (e) {
    rows.value = [];
  }
}

async function preview() {
  error.value = "";
  busy.value = true;
  try {
    const response = await employeeService.previewGratuity(props.employee.id, payload());
    worksheet.value = response.data?.worksheet || null;
    if (worksheet.value) rememberPrior(worksheet.value);
  } catch (e) {
    error.value = e?.response?.data?.message || "Gratuity could not be calculated.";
  } finally {
    busy.value = false;
  }
}

async function save() {
  error.value = "";
  busy.value = true;
  try {
    const response = await employeeService.scheduleGratuity(props.employee.id, payload());
    toast(response.data?.message || "Gratuity scheduled.");
    worksheet.value = response.data?.data?.worksheet || worksheet.value;
    if (props.context?.source_id) {
      linkedExisting.value = response.data?.data || { status: "scheduled" };
    }
    await load();
  } catch (e) {
    error.value = e?.response?.data?.message || "Gratuity could not be scheduled.";
  } finally {
    busy.value = false;
  }
}

async function remove(row) {
  try {
    await employeeService.cancelGratuity(props.employee.id, row.id);
    toast("Scheduled gratuity removed.");
    await load();
  } catch (e) {
    toast(e?.response?.data?.message || "Gratuity could not be removed.", "error");
  }
}

function payload() {
  return {
    gross_amount: form.gross_amount,
    service_start: form.service_start,
    service_end: form.service_end,
    payment_period: form.payment_period,
    destination: form.destination,
    scheme_type: form.scheme_type,
    source_type: props.context?.source_type || null,
    source_id: props.context?.source_id || null,
    prior_income: worksheet.value ? priorPayload() : [],
  };
}
</script>

<style>
body.gratuity-modal-open .modal-backdrop.show {
  opacity: 1;
  background-color: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
</style>
